import { z } from 'zod';

import { paymentMethodTypeSchema } from '~/features/checkout/models/user-payment-method';
import { Currency, currencySchema } from '~/features/internationalization/models/currency';

import { CheckoutError } from '../errors';
import { getStrikethroughPrice } from '../utils/get-strikethrough-price';

export const billingFrequencySchema = z.enum(['purchase', 'monthly', 'annually', '4month']);
export const checkoutTypeSchema = z.enum(['individual', 'team', 'team-pro', 'degree-application']);
export const checkoutPaymentTypeSchema = z.enum(['credit-card', 'saved-card', 'razorpay', 'external']);
export const paymentPlanTypeSchema = z.enum(['one_time', 'recurring', 'upfront_recurring']);

export const upcomingPaymentSchema = z.object({
  date: z.string(),
  original_amount: z.number(),
  original_amount_display: z.string(),
  payable_amount: z.number(),
  payable_amount_display: z.string(),
  discount_amount: z.number(),
  discount_amount_display: z.string(),
});

export const upcomingPaymentsSchema = z.array(upcomingPaymentSchema);

export const lineItemSchema = z.object({
  key: z.enum([
    'line_item.list_price',
    'line_item.percent_discount',
    'line_item.upfront_discount',
    'line_item.coupon_discount',
    'line_item.total',
    'line_item.renewal_amount',
    'line_item.india_gst',
  ]),
  description: z.string(),
  value: z.number(),
  value_display: z.string(),
  group: z.number(),
});

export const couponSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: z.string(),
  redeem_by: z.number(),
  currency: z.string().nullable(),
  amount_off: z.number().nullable(),
  percent_off: z.number().nullable(),
  free_trial: z.boolean().optional(),
});

export const skuAmountSchema = z.object({
  amount: z.number(),
  amount_display: z.string(),
  currency: currencySchema,
});

export const skuSchema = z.object({
  urn: z.string(),
  name: z.string(),
  status: z.string(),
  amount: skuAmountSchema,
  node_keys: z.array(z.string()),
  learning_plan_key: z.string().nullable(),
});

export const planAmountSchema = z.object({
  original_amount: z.number(),
  original_amount_display: z.string(),
  payable_amount: z.number(),
  payable_amount_display: z.string(),
  discount_amount: z.number(),
  discount_amount_display: z.string(),
  currency: currencySchema,
});

export const paymentPlanSchema = z.object({
  type: paymentPlanTypeSchema,
  description: z.string(),
  next_due_date: z.string().nullable(),
  upfront_amount: planAmountSchema,
  upfront_interval: z.string().nullable(),
  upfront_interval_count: z.number().nullable(),
  recurring_amount: planAmountSchema.optional(),
  recurring_interval: z.string().optional(),
  recurring_interval_count: z.number().optional(),
  unit_amount_per_month: z.number().optional(),
  unit_amount_per_month_display: z.string().optional(),
});

export const resultSchema = z.object({
  sku: skuSchema,
  price: planAmountSchema,
  payment_methods: z.array(paymentMethodTypeSchema),
  coupon: couponSchema.nullable(),
  payment_plans_v2: z.array(paymentPlanSchema),
  promotion: z.any(),
  // Note: team-pro checkout doesn't have the `original_amount_per_year_x` fields.
  original_amount_per_year: z.number().optional(),
  original_amount_per_year_display: z.string().optional(),
  original_amount_4_months: z.number().optional(),
  original_amount_4_months_display: z.string().optional(),
  coupon_applied: z.boolean(),
  coupon_applied_to_payment_plan: z.boolean(),
  upcoming_payments: upcomingPaymentsSchema.optional(),
  free_trial: z.boolean().optional(),
  full_amount: z.number().optional(),
  degree_application_amount: z.number().optional(),
  degree_application_amount_display: z.string().optional(),
});

export const checkoutSchema = z
  .object({
    version: z.string(),
    results: z.array(resultSchema).nonempty(),
    billingFrequency: billingFrequencySchema,
    couponCode: z.string().optional(),
    checkoutType: checkoutTypeSchema,
  })
  .transform((data) => {
    const checkoutData = data.results[0];

    const discounts = [];
    if (checkoutData.coupon && checkoutData.coupon_applied) {
      discounts.push({
        description: checkoutData.coupon.key,
        value: checkoutData.coupon.amount_off
          ? `-${checkoutData.coupon.amount_off}`
          : `-${checkoutData.coupon.percent_off}%`,
      });
    }

    const inputData = mapBillingFreqToInputData(data.billingFrequency);
    if (!inputData) {
      throw new CheckoutError({
        name: 'CHECKOUT_TRANSFORM_FAILED',
        message: 'Unable to transform checkout data: invalid billing frequency ' + data.billingFrequency,
      });
    }
    const { paymentPlanType, upfrontIntervalCount, recurringIntervalCount } = inputData;

    const paymentPlan = checkoutData.payment_plans_v2.find((paymentPlan) => {
      if (data.billingFrequency === billingFrequencySchema.enum['purchase']) {
        return paymentPlan.type === 'one_time';
      }
      if (data.billingFrequency === billingFrequencySchema.enum['4month']) {
        return paymentPlan.type === 'upfront_recurring' && paymentPlan.upfront_interval_count === 4;
      }
      if (data.billingFrequency === billingFrequencySchema.enum['annually']) {
        return paymentPlan.type === 'upfront_recurring' && paymentPlan.upfront_interval_count === 12;
      }
      return paymentPlan.type === 'recurring' && paymentPlan.recurring_interval_count === recurringIntervalCount;
    });

    if (!paymentPlan) {
      throw new CheckoutError({
        name: 'CHECKOUT_TRANSFORM_FAILED',
        message: 'Unable to transform checkout data: payment plan for recurring interval count not found',
        cause: JSON.stringify(data),
      });
    }

    const isFreeTrialCoupon = Boolean(checkoutData?.coupon?.free_trial);

    // A coupon could be compatible with a sku but not one of sku's payment plan, so check both.
    const couponCodeApplied = checkoutData.coupon_applied && checkoutData.coupon_applied_to_payment_plan;

    let payableAmount = paymentPlan.upfront_amount.payable_amount;
    let payableAmountDisplay = paymentPlan.upfront_amount.payable_amount_display;

    if (checkoutData.upcoming_payments) {
      payableAmount = checkoutData.upcoming_payments[0]?.payable_amount;
      payableAmountDisplay = checkoutData.upcoming_payments[0]?.payable_amount_display;
    }

    // Depending on whether the checkout is for a standalone degree application or as a part of a subscription, the amount comes from different places.
    let degreeApplicationAmount;
    let degreeApplicationAmountDisplay;
    if (paymentPlan.type === 'one_time') {
      degreeApplicationAmount = payableAmount;
      degreeApplicationAmountDisplay = payableAmountDisplay;
    } else {
      degreeApplicationAmount = checkoutData.degree_application_amount;
      degreeApplicationAmountDisplay = checkoutData.degree_application_amount_display;
    }

    return {
      skuUrn: checkoutData.sku.urn,
      skuName: checkoutData.sku.name,
      billingFrequency: data.billingFrequency,
      currency: paymentPlan.upfront_amount.currency as Currency,
      originalAmountDisplay: paymentPlan.upfront_amount.original_amount_display,
      strikeThroughAmountDisplay: getStrikethroughPrice({
        billingFrequency: data.billingFrequency as BillingFrequency,
        checkoutType: data.checkoutType,
        paymentPlan,
        checkoutResult: checkoutData,
      }),
      payableAmountDisplay,
      payableAmount,
      recurringAmountDisplay: paymentPlan?.recurring_amount?.payable_amount_display,
      recurringAmount: paymentPlan?.recurring_amount?.payable_amount,
      unitAmountPerMonthDisplay: paymentPlan.unit_amount_per_month_display,
      listAmountPerUnitPerMonthDisplay: checkoutData.sku.amount.amount_display,
      unitAmountPerMonth: paymentPlan.unit_amount_per_month,
      couponCode: checkoutData.coupon?.id,
      couponCodeApplied,
      couponPercentOff: checkoutData.coupon?.percent_off,
      isFreeTrial: checkoutData.free_trial || isFreeTrialCoupon,
      discounts: discounts,
      paymentPlanType: paymentPlanType,
      recurringIntervalCount: recurringIntervalCount,
      upfrontIntervalCount: upfrontIntervalCount,
      // If the request has a coupon but the response says the coupon has not been applied then the coupon is invalid.
      isCouponInvalid: data.couponCode && !couponCodeApplied,
      price: checkoutData.price,
      upcomingPayments: checkoutData.upcoming_payments,
      isFreeTrialCoupon,
      degreeApplicationAmount,
      degreeApplicationAmountDisplay,
    } as const;
  });

export type CheckoutType = z.infer<typeof checkoutTypeSchema>;
export type CheckoutPaymentType = z.infer<typeof checkoutPaymentTypeSchema>;
export type PaymentPlanType = z.infer<typeof paymentPlanTypeSchema>;
export type Checkout = z.infer<typeof checkoutSchema> & {
  captchaRequired: boolean;
  segmentAnonymousId: string | undefined;
};
export type CheckoutSku = z.infer<typeof skuSchema>;
export type CheckoutResult = z.infer<typeof resultSchema>;
export type CheckoutResponse = z.input<typeof checkoutSchema>;
export type CheckoutSkeleton = Partial<Checkout>;
export type PaymentPlan = z.infer<typeof paymentPlanSchema>;
export type PlanAmount = z.infer<typeof planAmountSchema>;
export type BillingFrequency = z.infer<typeof billingFrequencySchema>;
export type UpcomingPayment = z.infer<typeof upcomingPaymentSchema>;
export type UpcomingPayments = z.infer<typeof upcomingPaymentsSchema>;

// Convert the billing frequency presets to the input data for the checkout request.
export const mapBillingFreqToInputData = (
  billingFrequency: BillingFrequency
):
  | {
      paymentPlanType: PaymentPlanType;
      upfrontIntervalCount: 4 | 12 | undefined;
      recurringIntervalCount: 1 | undefined;
    }
  | undefined => {
  switch (billingFrequency) {
    case 'monthly':
      return {
        paymentPlanType: 'recurring',
        upfrontIntervalCount: undefined,
        recurringIntervalCount: 1,
      };
    case '4month':
      return {
        paymentPlanType: 'upfront_recurring',
        upfrontIntervalCount: 4,
        recurringIntervalCount: 1,
      };
    case 'annually':
      return {
        paymentPlanType: 'upfront_recurring',
        upfrontIntervalCount: 12,
        recurringIntervalCount: 1,
      };
    case 'purchase':
      return {
        paymentPlanType: 'one_time',
        upfrontIntervalCount: undefined,
        recurringIntervalCount: undefined,
      };
    default:
      console.error('Invalid billing frequency ' + billingFrequency);
      return undefined;
  }
};
